import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, User, Mail, Calendar, Target, BookOpen, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import API from "../../lib/api";
import { SURVEY_QUESTIONS, PERSONA_DEFINITIONS } from "../../config/surveyConfig";

export default function AdminResponseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/admin/responses/${id}`).then(r => { setResponse(r.data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Response not found"); });
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-[#a21caf] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!response) {
    return (
      <div className="nb-card text-center py-12">
        <h2 className="font-parkinsans text-2xl mb-2">Not found</h2>
        <p className="text-gray-500 mb-4">This response doesn't exist.</p>
        <button onClick={() => navigate("/admin/responses")} className="nb-btn-secondary">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back
        </button>
      </div>
    );
  }

  const personaDef = PERSONA_DEFINITIONS[response.persona] || {};
  const answers = response.raw_answers || {};

  // Generate summary
  const summary = [];
  summary.push(`${response.short_name} is a "${response.persona}" with a readiness score of ${response.total_score}/36.`);
  if (response.usage_level === "Regular" || response.usage_level === "Occasional") {
    summary.push(`Currently an ${response.usage_level.toLowerCase()} AI user.`);
  } else {
    summary.push(`Has ${response.usage_level === "Tried" ? "tried AI once or twice" : "not used AI yet"}.`);
  }
  if (response.concerns?.length) {
    summary.push(`Key concerns: ${response.concerns.slice(0, 3).join(", ")}.`);
  }
  if (response.learning_goals?.length) {
    summary.push(`Wants to learn: ${response.learning_goals.join(", ")}.`);
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(summary.join("\n"));
    toast.success("Summary copied!");
  };

  const getQuestionLabel = (qid) => {
    const q = SURVEY_QUESTIONS.find(q => q.id === qid);
    return q?.question || qid;
  };

  const isConditionalShown = (qid) => {
    const q = SURVEY_QUESTIONS.find(q => q.id === qid);
    return q?.dependsOn != null;
  };

  return (
    <div className="space-y-6" data-testid="response-detail-page">
      <button onClick={() => navigate("/admin/responses")} className="nb-btn-secondary text-sm py-2 px-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> All Responses
      </button>

      {/* Header */}
      <div className="nb-card" data-testid="response-header">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="font-parkinsans text-2xl sm:text-3xl mb-1">{response.short_name}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{response.email}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(response.created_at).toLocaleString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="nb-badge bg-[#f5d0fe] text-base px-4 py-2" data-testid="detail-persona">
              {personaDef.emoji || response.persona_emoji} {response.persona}
            </span>
            <span className="nb-badge bg-white text-base px-4 py-2 font-bold text-[#a21caf]" data-testid="detail-score">
              {response.total_score}/36
            </span>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="nb-card bg-[#f5d0fe]/20" data-testid="summary-block">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-parkinsans text-lg">Readiness Snapshot</h3>
          <button onClick={handleCopy} className="p-2 rounded-lg border-2 border-[#111] bg-white hover:bg-[#f5d0fe] transition-colors shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]" data-testid="copy-detail-btn">
            <Copy className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
        <ul className="space-y-2">
          {summary.map((s, i) => (
            <li key={i} className="text-sm flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a21caf] mt-1.5 flex-shrink-0" />
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Info cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="nb-card-sm" data-testid="info-usage">
          <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
            <span className="text-xs font-bold text-gray-500 uppercase">Usage Level</span>
          </div>
          <p className="font-semibold">{response.usage_level}</p>
        </div>
        <div className="nb-card-sm" data-testid="info-rating">
          <div className="flex items-center gap-2 mb-2"><User className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
            <span className="text-xs font-bold text-gray-500 uppercase">Self Rating</span>
          </div>
          <p className="font-semibold">{response.self_rating}</p>
        </div>
        <div className="nb-card-sm" data-testid="info-persona-desc">
          <div className="flex items-center gap-2 mb-2"><BookOpen className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
            <span className="text-xs font-bold text-gray-500 uppercase">Persona Insight</span>
          </div>
          <p className="text-sm text-gray-600">{response.persona_description || personaDef.description}</p>
        </div>
      </div>

      {/* Concerns and Learning Goals */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="nb-card" data-testid="detail-concerns">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#f59e0b]" strokeWidth={2.5} />
            <h3 className="font-parkinsans text-base">Concerns</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {response.concerns?.map((c, i) => (
              <span key={i} className="nb-badge bg-[#fef3c7] text-xs">{c}</span>
            ))}
            {(!response.concerns || response.concerns.length === 0) && <p className="text-sm text-gray-400">None selected</p>}
          </div>
        </div>
        <div className="nb-card" data-testid="detail-learning">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-[#3b82f6]" strokeWidth={2.5} />
            <h3 className="font-parkinsans text-base">Learning Goals</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {response.learning_goals?.map((g, i) => (
              <span key={i} className="nb-badge bg-[#dbeafe] text-xs">{g}</span>
            ))}
            {(!response.learning_goals || response.learning_goals.length === 0) && <p className="text-sm text-gray-400">None selected</p>}
          </div>
        </div>
      </div>

      {/* All answers */}
      <div className="nb-card" data-testid="all-answers">
        <h3 className="font-parkinsans text-lg mb-4">All Answers</h3>
        <div className="space-y-4">
          {SURVEY_QUESTIONS.map(q => {
            const val = answers[q.id];
            if (val === undefined || val === null || val === "") return null;
            return (
              <div key={q.id} className="border-b border-gray-100 pb-3 last:border-0" data-testid={`answer-${q.id}`}>
                <div className="flex items-start gap-2 mb-1">
                  <p className="text-sm font-semibold text-[#111]">{q.question}</p>
                  {isConditionalShown(q.id) && (
                    <span className="text-[10px] bg-[#f5d0fe] px-1.5 py-0.5 rounded font-bold whitespace-nowrap">CONDITIONAL</span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {Array.isArray(val) ? val.join(", ") : String(val)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
